import React, { useEffect, useState } from 'react';
import api from '../api';
import Modal from '../components/Modal';
import { hinweis } from '../components/Hinweise';

const FARBEN = ['#0066cc','#2e7d32','#6a1b9a','#e65100','#c62828','#00838f','#558b2f','#4527a0'];
const ICONS  = ['🏊','🚌','📚','🎉','⚽','🏋️','🤽','🎯','🏄','🚴','🧘','🎪'];

export default function AngebotTypen() {
  const [typen, setTypen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | {mode:'neu'|'edit', data?}
  const [form, setForm] = useState({ name: '', icon: '📌', farbe: '#0099cc', reihenfolge: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const r = await api.get('/api/admin/angebot-typen');
      setTypen(r.data);
    } catch { setError('Typen konnten nicht geladen werden'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNeu = () => {
    setForm({ name: '', icon: '📌', farbe: '#0099cc', reihenfolge: typen.length });
    setModal({ mode: 'neu' });
  };

  const openEdit = (t) => {
    setForm({ name: t.name, icon: t.icon, farbe: t.farbe, reihenfolge: t.reihenfolge });
    setModal({ mode: 'edit', data: t });
  };

  const handleSave = async () => {
    if (!form.name.trim()) return hinweis.fehler('Name ist Pflicht');
    setSaving(true);
    try {
      if (modal.mode === 'neu') await api.post('/api/admin/angebot-typen', form);
      else await api.put(`/api/admin/angebot-typen/${modal.data.id}`, form);
      await load();
      setModal(null);
    } catch (err) { hinweis.fehler(err.response?.data?.error || 'Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Typ „${t.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/api/admin/angebot-typen/${t.id}`);
      await load();
    } catch (err) { hinweis.fehler(err.response?.data?.error || 'Fehler beim Löschen'); }
  };

  const handleToggle = async (t) => {
    try {
      await api.put(`/api/admin/angebot-typen/${t.id}`, { aktiv: !t.aktiv });
      await load();
    } catch { hinweis.fehler('Fehler'); }
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Angebot-Typen</h2>
        <button className="btn btn-primary btn-sm" onClick={openNeu}>+ Neuer Typ</button>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
        Kategorien für deine Angebote (Kurs, Fahrt, Seminar…). Frei konfigurierbar.
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="data-table">
            <thead>
              <tr><th>Typ</th><th>Farbe</th><th>Reihenf.</th><th>Status</th><th>Aktionen</th></tr>
            </thead>
            <tbody>
              {typen.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>Noch keine Typen angelegt</td></tr>
              )}
              {typen.map(t => (
                <tr key={t.id}>
                  <td>
                    <span style={{ fontSize: '1.2rem', marginRight: '0.5rem' }}>{t.icon}</span>
                    <strong>{t.name}</strong>
                  </td>
                  <td>
                    <span style={{
                      display: 'inline-block', width: 20, height: 20, borderRadius: 4,
                      background: t.farbe, border: '1px solid rgba(0,0,0,.1)', verticalAlign: 'middle',
                      marginRight: 6,
                    }} />
                    {t.farbe}
                  </td>
                  <td>{t.reihenfolge}</td>
                  <td>
                    <span className={`status-badge ${t.aktiv ? 'status-aktiv' : 'status-inaktiv'}`}>
                      {t.aktiv ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(t)}>Bearbeiten</button>
                      <button className={`btn btn-sm ${t.aktiv ? 'btn-danger' : 'btn-success'}`} onClick={() => handleToggle(t)}>
                        {t.aktiv ? 'Aus' : 'Ein'}
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(t)}>Löschen</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal title={modal.mode === 'neu' ? 'Neuer Typ' : `Typ bearbeiten: ${modal.data.name}`} onClose={() => setModal(null)} size="sm">
          <div className="form-group">
            <label className="form-label">Name</label>
            <input className="form-control" value={form.name} maxLength={100}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Icon</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
              {ICONS.map(ic => (
                <button key={ic} type="button" onClick={() => setForm(p => ({ ...p, icon: ic }))}
                  style={{
                    fontSize: '1.4rem', padding: '4px 8px', border: '2px solid',
                    borderColor: form.icon === ic ? 'var(--primary)' : 'var(--border)',
                    borderRadius: 6, background: form.icon === ic ? 'var(--primary-light)' : '#fff',
                    cursor: 'pointer',
                  }}>
                  {ic}
                </button>
              ))}
            </div>
            <input className="form-control" value={form.icon} maxLength={10} placeholder="Oder eigenes Emoji eingeben"
              onChange={e => setForm(p => ({ ...p, icon: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Farbe</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
              {FARBEN.map(f => (
                <button key={f} type="button" onClick={() => setForm(p => ({ ...p, farbe: f }))}
                  style={{
                    width: 28, height: 28, borderRadius: 6, background: f, cursor: 'pointer',
                    border: form.farbe === f ? '3px solid #333' : '2px solid rgba(0,0,0,.1)',
                  }} />
              ))}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input type="color" value={form.farbe} onChange={e => setForm(p => ({ ...p, farbe: e.target.value }))}
                style={{ width: 40, height: 32, border: 'none', cursor: 'pointer', padding: 0 }} />
              <input className="form-control" value={form.farbe} maxLength={7}
                onChange={e => setForm(p => ({ ...p, farbe: e.target.value }))}
                style={{ fontFamily: 'monospace' }} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Reihenfolge</label>
            <input type="number" className="form-control" value={form.reihenfolge} min={0}
              onChange={e => setForm(p => ({ ...p, reihenfolge: parseInt(e.target.value) || 0 }))} />
          </div>
          <div className="modal-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => setModal(null)}>Abbrechen</button>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              {saving ? 'Speichern…' : 'Speichern'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
